"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { getCategories } from "@/firebase/firestoreServices";
import { SkeletonCategory } from "./SkeletonLoaders";
import type { Category } from "@/types";
import { motion } from "framer-motion";
import LazyImage from "@/components/LazyImage";

interface CategorySectionProps {
  initialCategories?: Category[];
}

const CategorySection = ({ initialCategories = [] }: CategorySectionProps) => {
  const [categories, setCategories] = useState<Category[]>(initialCategories);
  const [loading, setLoading] = useState(initialCategories.length === 0);

  useEffect(() => {
    if (initialCategories.length > 0) return;
    const fetch = async () => {
      try {
        const data = await getCategories();
        setCategories(data as Category[]);
      } catch (e) {
        console.error("Error fetching categories:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [initialCategories]);

  if (loading) {
    return (
      <div className="space-y-5">
        <h2 className="text-sm font-semibold text-primary tracking-[0.2em] uppercase text-center">Shop by Category</h2>
        <div className="flex gap-5 md:gap-8 justify-center flex-wrap">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCategory key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <section className="space-y-5">
      <div className="text-center">
        <h2 className="text-sm font-semibold text-primary tracking-[0.2em] uppercase">Shop by Category</h2>
        <p className="text-xs text-muted-foreground mt-1">Explore our fresh collection</p>
      </div>
      <div className="flex gap-5 md:gap-8 justify-center flex-wrap">
        {categories.map((cat, i) => (
          <motion.div
            key={cat.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.4 }}
          >
            <Link
              href={`/category/${cat.id}`}
              className="flex flex-col items-center gap-2.5 group"
            >
              <motion.div
                whileHover={{ scale: 1.08, y: -4 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-border group-hover:border-primary shadow-md group-hover:shadow-xl transition-all duration-300 bg-muted"
              >
                {cat.image ? (
                  <LazyImage
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover"
                    wrapperClassName="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-2xl font-bold bg-gradient-to-br from-muted to-accent">
                    {cat.name[0]}
                  </div>
                )}
              </motion.div>
              <span className="text-xs md:text-sm font-medium text-foreground text-center leading-tight max-w-[80px] group-hover:text-primary transition-colors">
                {cat.name}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default CategorySection;
